<template>
	<div class="page-edit-admin">
		<div class="admin__wrap">
			<h2 class="section-title admin__title">{{ this.label }} Admin</h2>
			<div class="notification-wrap-side notification-wrap-side--center">
				<PartNotifications :data="notificationsData" v-if="notificationsData.msg" />
			</div>
		</div>
		<ValidationObserver ref="adminForm">
			<form class="admin__form" @submit.prevent="updateAdmin">
				<div class="error-messages__list" v-if="errors.length">
					<span class="error-messages__title">
						<SvgIcon class="error-messages__icon error-messages__icon--exclamation" iconName="ico-exclamation-circle"/>
						Please fill in all required fields:
					</span>
					<ul>
						<li class="error-messages__item" v-for="error in errors" :key="error">{{ error }}</li>
					</ul>
				</div>
				<div class="admin__form-half">
					<div class="admin__form-input-box">
						<div class="property-form__input">
							<ValidationProvider rules="required" v-slot="{ errors, classes }" class="error-message-holder error-message-holder--block">
								<PartInputBasic v-model="admin.first_name" :class="classes" ref="first_name" :data="DataFirstName"/>
								<span class="error-message" v-if="errors[0]">{{ errors[0]}}</span>
							</ValidationProvider>
						</div>

						<div class="property-form__input">
							<ValidationProvider rules="required" v-slot="{ errors, classes }" class="error-message-holder error-message-holder--block">
								<PartInputBasic v-model="admin.last_name" :class="classes" ref="last_name" :data="DataLastName"/>
								<span class="error-message" v-if="errors[0]">{{ errors[0]}}</span>
							</ValidationProvider>
						</div>
					</div>

					<div class="property-form__input property-form__input--upload-img">
						<BlockUploadImage v-model="admin.photo" ref="photo" :data="DataUploadImage"/>
					</div>
				</div>
				<div class="property-form__input">
					<ValidationProvider rules="required|email" v-slot="{ errors, classes }" class="error-message-holder error-message-holder--block">
						<PartInputBasic v-model="admin.email" :class="classes" ref="email" :data="DataEmailName"/>
						<span class="error-message" v-if="errors[0]">{{ errors[0]}}</span>
						<span class="error-message" v-else-if="emailExists">{{ emailExistsMessage }}</span>
					</ValidationProvider>
				</div>
				<!-- This field has been newly added for alternative email -->
				<div class="property-form__input">
					<ValidationProvider rules="required|email" v-slot="{ errors, classes }" class="error-message-holder error-message-holder--block">
						<PartInputBasic v-model="admin.alternative_email" :class="classes" ref="alternative_email" :data="DataAlternativeEmail"/>
						<span class="error-message" v-if="errors[0]">{{ errors[0]}}</span>
						<span class="error-message" v-else-if="alternativeEmailExists">{{ AlternativEmailExistsMessage }}</span>
					</ValidationProvider>
				</div>

				<div class="property-form__input">
					<ValidationProvider :rules="`${LandlinePhone ? 'min:5' : ''}`" v-slot="{ errors, classes }" class="error-message-holder error-message-holder--block">
						<PartInputBasic v-model="admin.landline_phone" :class="classes" ref="landline_phone" :data="DataLandlinePhone"/>
						<span class="error-message" v-if="errors[0]">{{ errors[0]}}</span>
					</ValidationProvider>
				</div>

				<div class="property-form__input">
					<PartInputBasic v-model="admin.phone_ext" ref="phone_ext" :data="DataPhoneExt"/>
				</div>

				<div class="property-form__input">
					<ValidationProvider rules="required|min:5" v-slot="{ errors, classes }" class="error-message-holder error-message-holder--block">
						<PartInputBasic v-model="admin.cellphone" :class="classes" ref="cellphone" :data="DataCellphone"/>
						<span class="error-message" v-if="errors[0]">{{ errors[0]}}</span>
					</ValidationProvider>
				</div>

				<div class="admin__btn-wrap">
					<a class="btn admin__btn-sm" id="update_admin-button" @click="updateAdmin()">
						<SvgIcon class="save-icon" iconName="ico-save"/>
						Save
					</a>
					<router-link class="btn btn--white btn--icon  admin__btn-sm" id="back_admin-button" :to="{ name: 'Admins' }">
						<span>Cancel</span>
					</router-link>
				</div>
			</form>
		</ValidationObserver>
		<transition name="fade" appear>
			<PartLoaderBig v-if="pageLoader"/>
		</transition>
	</div>
</template>
<script>
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate';
import { required, email, min } from 'vee-validate/dist/rules';
import PartInputBasic from '@/components/parts/PartInputBasic';
import SvgIcon from '@/components/base/SvgIcon/SvgIcon';
import PartNotifications from '@/components/parts/PartNotifications';
import BlockUploadImage from '@/components/blocks/BlockUploadImage';
import PartLoaderBig from '@/components/parts/PartLoaderBig';

extend('email', {
	...required,
	message: 'This field is mandatory.'
});

extend('email', {
	...email,
	message: 'Email must be in correct format.'
});

extend('required', {
	...required,
	message: 'This field is mandatory.'
});

extend('min', {
	...min,
	message: 'Minimum number of characters is 5.'
});

export default {
	props: {
		edit: Boolean,
		myAccount: Boolean,
		data: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			errors: [],
			pageLoader: false,
			LandlinePhone: false,
			emailExists: false,
			emailExistsMessage: 'This email is already in use!',
			AlternativEmailExistsMessage: 'This alternative email is already in use!',
			alternativeEmailExists: false,
			label: this.edit ? 'Update' : 'Create New',
			notificationsData: {
				type: '',
				msg: ''
			},
			admin: {
				id: this.edit ? this.$route.params.id : 0,
				first_name: '',
				last_name: '',
				role: 'admin',
				email: '',
				landline_phone: '',
				phone_ext: '',
				cellphone: '',
				photo: '',
				alternative_email: ''
			},
			DataFirstName: {
				label: 'First Name',
				placeholder: 'Enter First Name',
				inputId: 'first_name-input',
				labelId: 'first_name-label',
				mandatoryFiled: true
			},
			DataLastName: {
				label: 'Last Name',
				placeholder: 'Enter Last Name',
				inputId: 'last_name-input',
				labelId: 'last_name-label',
				mandatoryFiled: true
			},
			DataEmailName: {
				label: 'Email',
				placeholder: 'Enter Email',
				inputId: 'email-input',
				labelId: 'email-label',
				mandatoryFiled: true
			},
			DataAlternativeEmail: {
				label: 'Alternative Email',
				placeholder: 'Enter Email',
				inputId: 'alternativeEmailAdmin-input',
				mandatoryFiled: true,
				labelId: 'alternativeEmailAdmin-label'
			},
			DataLandlinePhone: {
				label: 'Landline Phone',
				placeholder: 'Enter Landline Phone',
				inputId: 'landline-input',
				labelId: 'landline-label',
				mandatoryFiled: false,
				type: 'text'
			},
			DataPhoneExt: {
				label: 'Phone ext',
				placeholder: 'Enter Phone ext',
				inputId: 'phone_ext-input',
				labelId: 'phone_ext-label',
				mandatoryFiled: false,
				type: 'text'
			},
			DataCellphone: {
				label: 'Cellphone',
				placeholder: 'Enter Cellphone',
				inputId: 'cellphone-input',
				labelId: 'cellphone-label',
				mandatoryFiled: true,
				type: 'text'
			},
			DataUploadImage: {}
		};
	},
	components: {
		ValidationProvider,
		ValidationObserver,
		PartInputBasic,
		SvgIcon,
		PartNotifications,
		BlockUploadImage,
		PartLoaderBig
	},
	watch: {
		admin: {
			handler(val) {
				this.LandlinePhone = !!val.landline_phone;
			},
			deep: true
		},
		user() {
			return this.$store.getters.getUser;
		}
	},
	methods: {
		updateAdmin() {
			this.pageLoader = true;
			const action = this.myAccount ? 'updateMyAccount' : 'updateAdmin';
			this.$refs.adminForm.validate().then(success => {
				if (success) {
					this.$store.dispatch(action, this.admin).then(() => {
						if (!this.user) {
							this.user = JSON.parse(localStorage.getItem('user'));
						}
						this.emailExists = false;
						this.alternativeEmail = false;
						setTimeout(() => {
							this.myAccount ? this.backOnProperties() : this.backOnListing();
							this.pageLoader = false;
						}, 1000);

						if (this.myAccount || this.user.id === this.admin.id) {
							// commit change in state for update my account
							this.$store.commit('updateUserAccount', this.admin);
						}
					}).catch((error) => {
						this.pageLoader = false;
						this.errors = [];
						if (error.response.data.errors.email && error.response.data.errors.email[0].includes('The email has already been taken')) {
							this.emailExists = true;
							this.errors.push('The email has already been taken.');
						}
						if (error.response.data.errors.alternative_email && error.response.data.errors.alternative_email[0].includes('The alternative email has already been taken')) {
							this.alternativeEmailExists = true;
							this.errors.push('The alternative email has already been taken.');
						}
					});
				} else {
					this.pageLoader = false;
					// error messages list
					this.createErrorList();
					window.scrollTo({
						top: 0,
						behavior: 'smooth'
					});
				}
			});
		},
		createErrorList() {
			this.errors = [];
			if (this.admin.first_name && this.admin.last_name && this.admin.email && this.admin.alternative_email &&
			this.admin.cellphone && (this.admin.cellphone && (this.admin.cellphone.length >= 5)) &&
			(this.admin.alternative_email && this.validAlternativeEmail(this.admin.alternative_email)) &&
			(this.admin.email && this.validEmail(this.admin.email))) return true;
			
			if (!this.admin.first_name) this.errors.push('First Name is required.');
			if (!this.admin.last_name) this.errors.push('Last Name is required.');
			if (!this.admin.cellphone) this.errors.push('Phone is required.');
			if (this.admin.cellphone && this.admin.cellphone.length <= 5) this.errors.push('Phone must contain valid information.');
			if (!this.admin.email) {
				this.errors.push('Email is required.');
			} else if (this.admin.email && !this.validEmail(this.admin.email)) {
				this.errors.push('Email must contain valid information.');
			}
			if (!this.admin.alternative_email) {
				this.errors.push('Alternative email is required.');
			}
			if (this.admin.alternative_email && !this.validAlternativeEmail(this.admin.alternative_email)) {
				this.errors.push('Alternative email must contain valid information.');
			}
			return false;
		},
		validEmail: function (email) {
			const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
			return re.test(email);
		},
		validAlternativeEmail: function (alternativeEmail) {
			const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
			return re.test(alternativeEmail);
		},
		notification(msg, status) {
			this.notificationsData.msg = msg;
			this.notificationsData.type = status === 200 ? 'success' : 'danger';

			setTimeout(() => {
				this.notificationsData.msg = '';
				this.notificationsData.type = '';
			}, 2000);
		},
		// Super admin go on Admin listing
		backOnListing() {
			this.$router.push({ name: 'Admins' });
		},
		// Admin go back on Properties
		backOnProperties() {
			this.$router.push({ name: 'Properties' });
		}
	},
	created() {
		if (this.edit) {
			const action = this.myAccount ? 'editMyAccount' : 'fetchAdmin';
			this.$store.dispatch(action, !this.myAccount ? this.admin.id : '').then(response => {
				this.admin = response.data.admin;
				return this.admin;
			}).then(response => {
				for (const key in response) {
					if (this.$refs[key]) {
						this.$refs[key].setValue(this.admin[key]);
					}
				}
			});
		}
	}
};
</script>
<style lang="scss" scoped>
.page-edit-admin {
	padding-top: 15px;
}
.admin__form {
	max-width: 700px;
	padding-bottom: 20px;
}
.admin__wrap {
	display: flex;
	align-items: center;
	margin-bottom: 20px;
	flex-wrap: wrap;
}
.admin__btn-icon {
    display: inline-block;
    padding-right: 7px;
    font-size: 11px;
    height: 14px;
}
.admin__btn-wrap {
	display: flex;
    align-items: center;
    margin-bottom: 20px;
	justify-content: flex-start;

	.admin__btn-sm {
		max-width: 147px;

		&:not(:first-child) {
			margin-left: 50px;
		}
		@include mq(400px) {
			max-width: 120px;
			min-width: auto;
		}
	}
}

.save-icon {
	margin-right: 11px;
	svg {
		font-size: 20px;
		color: $white;
	}
}

.admin__title {
	display: inline-block;
	margin-right: 25px;
	text-align: left;
	margin-bottom: 0;

	@include mq(400px) {
		margin-right: 20px;
	}
}

.admin__form-half {
	display: flex;

	@include mq($md) {
		flex-direction: column;
	}
}

.admin__form-input-box {
	max-width: 70%;
	flex: 0 0 70%;

	@include mq($md) {
		flex: 0 0 100%;
		max-width: 100%;
		order: 2;
	}
}

.property-form__input--upload-img {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	width: 100%;

	@include mq($md)  {
		justify-content: flex-start;
		margin-bottom: 55px;
	}
}

.error-message {
	bottom: -19px;
	position: absolute;
	display: inline-block;
	transform: none;
}
</style>
